import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthState } from '@/lib/services/auth/auth.query'
import { useSalesOrderAssignees } from '@/lib/services/sales-orders/sales-orders.query'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '@sentry/react'
import { CircleUserRound, UserRound } from 'lucide-react'
import { Suspense, type ReactNode } from 'react'
import type { AuthUser } from '@/lib/services/auth/auth.types'
import type { SalesOrderSearchRecord } from '@/lib/services/sales-orders/sales-order.schema'
import type { SalesOrderAssignee } from '@/lib/services/sales-orders/sales-orders.types'

export interface SalesOrderFilterOption {
  value: string
  label: ReactNode
  icon?: ReactNode
}

export interface SalesOrderAssignmentSelectProps {
  assignees?: readonly SalesOrderAssignee[]
  currentUserId?: AuthUser['id'] | null
  filters?: Pick<SalesOrderSearchRecord, 'assignment_filter'>
  onValueChange?: (key: 'assignment_filter', value: string) => void
  options?: readonly SalesOrderFilterOption[]
  triggerClassName?: string
}

type SalesOrderAssignmentFilterProps = SalesOrderAssignmentSelectProps & {
  showAssigneeOptions?: boolean
}

export function SalesOrderAssignmentSelect({
  assignees = [],
  currentUserId,
  filters = {},
  onValueChange,
  options = [],
  triggerClassName,
}: SalesOrderAssignmentSelectProps) {
  const value = filters.assignment_filter ?? 'all'
  const isFiltered = value !== 'all'
  const currentUserAssignee = assignees.find(
    (assignee) => Number(assignee.id) === Number(currentUserId)
  )
  const otherAssignees = assignees.filter(
    (assignee) => Number(assignee.id) !== Number(currentUserId)
  )
  const hasAssigneeOptions = Boolean(currentUserAssignee || otherAssignees.length)
  const isSelectedAssigneeMissing =
    value.startsWith('user:') && !assignees.some((assignee) => `user:${assignee.id}` === value)

  return (
    <Select
      value={value}
      onValueChange={(nextValue) => onValueChange?.('assignment_filter', nextValue)}
    >
      <SelectTrigger
        shape="pill"
        selected={isFiltered}
        className={cn('w-fit min-w-[8.75rem]', triggerClassName)}
      >
        <SelectValue>{isSelectedAssigneeMissing ? 'Assigned user' : undefined}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((preset) => (
          <SelectItem key={preset.value} value={preset.value}>
            {preset.icon}
            {preset.label}
          </SelectItem>
        ))}
        {hasAssigneeOptions ? (
          <>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>Assigned to</SelectLabel>
              {currentUserAssignee ? (
                <SelectItem value={`user:${currentUserAssignee.id}`}>
                  <CircleUserRound className="size-4" />
                  Assigned to me
                </SelectItem>
              ) : null}
              {otherAssignees.map((assignee) => (
                <SelectItem key={assignee.id} value={`user:${assignee.id}`}>
                  <UserRound className="size-4" />
                  {assignee.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </>
        ) : null}
      </SelectContent>
    </Select>
  )
}

function OrganizationSalesOrderAssignmentSelect(props: SalesOrderAssignmentSelectProps) {
  const { data: assignees = [] } = useSalesOrderAssignees()
  const { data: { user } = {} } = useAuthState()

  return <SalesOrderAssignmentSelect {...props} assignees={assignees} currentUserId={user?.id} />
}

export function SalesOrderAssignmentFilter({
  showAssigneeOptions = false,
  ...props
}: SalesOrderAssignmentFilterProps) {
  const basicAssignmentSelect = <SalesOrderAssignmentSelect {...props} />

  if (!showAssigneeOptions) {
    return basicAssignmentSelect
  }

  return (
    <ErrorBoundary fallback={() => basicAssignmentSelect}>
      <Suspense fallback={basicAssignmentSelect}>
        <OrganizationSalesOrderAssignmentSelect {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
